/**
 * Performance3DCanvas — Thin DOM/event shell for 3D performance mode.
 *
 * Delegates all rendering, mesh lifecycle, and scene management to
 * PerformanceRenderer (accessed via the video registry).
 * This component owns the canvas element, DOM events, and RAF loop.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useScope } from '../../contexts/ScopeContext';
import { useAppState } from '../../contexts/AppContext';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { useRafLoop } from '@/ui/hooks/useRafLoop';
import { useDragControls, type ModifierKeys } from '@/ui/hooks/useDragControls';
import { resolveVisualiser } from '@/ui/performance/PerformanceStylesheet';
import { webRuntimeApi } from '@/runtime/web';
import { SCALE } from './PerformanceRenderer';
import { TweakablePanel } from './TweakablePanel';
import type { PerformanceRenderer } from './PerformanceRenderer';

/** Convert client pixel coordinates to NDC relative to a container element */
function clientToNDC(clientX: number, clientY: number, container: HTMLElement): { x: number; y: number } {
  const rect = container.getBoundingClientRect();
  return {
    x: ((clientX - rect.left) / rect.width) * 2 - 1,
    y: -((clientY - rect.top) / rect.height) * 2 + 1,
  };
}

export function Performance3DCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useDragControls<HTMLDivElement>({
    onDrag: handleOrbitDrag,
    onWheel: handleOrbitWheel,
    momentum: true,
    friction: 0.08,
  });

  const { instanceId, definitionState, selectedNodeIds, setSelectedNodeIds, definitionId } = useScope();
  const { stylesheet, setInspecting } = useAppState();
  const { instanceState, tweakValue, addNode } = useRuntime();

  // Drag-to-move state (DOM-level, stays in the component)
  const pickedRef = useRef<{ nodeId: string; resizing: boolean; repeaterNodeId: string | null } | null>(null);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const dragOffsetRef = useRef<{ x: number; z: number }>({ x: 0, z: 0 });
  const groupOffsetsRef = useRef<Map<string, { x: number; z: number }> | null>(null);
  const didDragRef = useRef(false);
  const [activeCamera, setActiveCamera] = useState('default');

  // ─── Activate / deactivate renderer ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    webRuntimeApi.activatePerformanceView(instanceId, canvas);

    // Sync active camera state from restored renderer
    const initialRenderer = webRuntimeApi.getPerformanceRenderer(instanceId);
    if (initialRenderer) {
      setActiveCamera(initialRenderer.getActiveCamera());
    }

    // Initial size
    const container = containerRef.current;
    if (container) {
      const renderer = webRuntimeApi.getPerformanceRenderer(instanceId);
      if (renderer) {
        const { clientWidth: w, clientHeight: h } = container;
        renderer.setSize(w, h);
      }
    }

    // Resize observer
    const ro = new ResizeObserver((entries) => {
      const renderer = webRuntimeApi.getPerformanceRenderer(instanceId);
      if (!renderer) return;
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          renderer.setSize(width, height);
        }
      }
    });
    if (container) ro.observe(container);

    return () => {
      ro.disconnect();
      webRuntimeApi.deactivatePerformanceView(instanceId);
    };
  }, [instanceId]);

  // ─── RAF loop: syncNodes + render ───
  useRafLoop(() => {
    if (!definitionState) return;

    const renderer = webRuntimeApi.getPerformanceRenderer(instanceId);
    if (!renderer) return;

    renderer.syncNodes(
      { nodes: definitionState.nodes, positions: definitionState.positions, api: definitionState.api },
      instanceState,
      stylesheet,
      tweakValue,
    );
    renderer.render();
  }, [definitionState, instanceId, instanceState, stylesheet, tweakValue]);

  // ─── Helper: get renderer (typed) ───
  function getRenderer(): PerformanceRenderer | null {
    return webRuntimeApi.getPerformanceRenderer(instanceId);
  }

  // ─── Orbit controls ───
  function handleOrbitDrag(dx: number, dy: number, modifiers: ModifierKeys) {
    if (pickedRef.current) return; // node drag handled by onPointerMove
    const renderer = getRenderer();
    if (!renderer) return;

    if (renderer.getActiveCamera() === 'plan') {
      // Plan view: always pan
      renderer.updatePan(dx, dy);
    } else if (modifiers.shift || modifiers.meta) {
      // Pan (shift or cmd)
      renderer.updatePan(dx, dy);
    } else if (modifiers.alt) {
      // Roll (alt)
      renderer.updateRoll(dx);
    } else {
      // Normal orbit
      renderer.updateOrbit(dx * 0.005, -dy * 0.005);
    }
  }

  function handleOrbitWheel(delta: number, mouseX: number, mouseY: number) {
    const renderer = getRenderer();
    const container = containerRef.current;
    if (!renderer || !container || !definitionState) {
      return;
    }

    // Check if hovering over a node mesh — if so, tweak its value
    const rect = container.getBoundingClientRect();
    const ndcX = (mouseX / rect.width) * 2 - 1;
    const ndcY = -(mouseY / rect.height) * 2 + 1;

    const tweakInfo = renderer.getNodeTweakInfo(
      ndcX, ndcY,
      { nodes: definitionState.nodes, positions: definitionState.positions },
      instanceState,
      stylesheet,
    );

    if (tweakInfo) {
      const { nodeId, endpointId, ep, currentValue } = tweakInfo;
      const range = (ep.max ?? 1) - (ep.min ?? 0);
      const step = range * 0.01;
      const newVal = Math.max(ep.min ?? 0, Math.min(ep.max ?? 1, (currentValue ?? ep.min ?? 0) - delta * step));
      tweakValue(instanceId, nodeId, endpointId, newVal);
      return;
    }

    // Otherwise zoom
    renderer.updateZoom(delta * 0.1);
  }

  // ─── Node picking (pointerdown on canvas) ───
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const renderer = getRenderer();
    const container = containerRef.current;
    if (!renderer || !container) return;

    const ndc = clientToNDC(e.clientX, e.clientY, container);
    const nodeId = renderer.raycast(ndc.x, ndc.y);

    if (nodeId) {
      const mesh = renderer.getNodeMesh(nodeId);
      if (mesh) {
        const resizing = e.metaKey || e.ctrlKey;
        const repeaterNodeId = renderer.getRepeaterNodeId(nodeId);
        pickedRef.current = { nodeId, resizing, repeaterNodeId };
        mousePosRef.current = { x: e.clientX, y: e.clientY };
        didDragRef.current = false;
        groupOffsetsRef.current = null;

        const planeHit = renderer.raycastToPlane(ndc.x, ndc.y);
        if (planeHit) {
          dragOffsetRef.current = {
            x: mesh.position.x - planeHit.x,
            z: mesh.position.z - planeHit.z,
          };

          // Group drag: compute per-mesh offsets for all repeater siblings
          // Alt/Option = individual instance mode, otherwise group
          if (repeaterNodeId && !e.altKey) {
            const groupIds = renderer.getRepeaterGroup(nodeId);
            if (groupIds) {
              const offsets = new Map<string, { x: number; z: number }>();
              for (const id of groupIds) {
                const m = renderer.getNodeMesh(id);
                if (m) {
                  offsets.set(id, {
                    x: m.position.x - planeHit.x,
                    z: m.position.z - planeHit.z,
                  });
                }
              }
              groupOffsetsRef.current = offsets;
            }
          }
        }
      }
    }
  }, [instanceId]);

  const handlePointerUp = useCallback(() => {
    if (pickedRef.current) {
      const { nodeId, repeaterNodeId } = pickedRef.current;
      const renderer = getRenderer();

      if (!didDragRef.current) {
        // Click without drag — toggle selection
        // For repeater children, select the repeater node itself
        const selectId = repeaterNodeId ?? nodeId;
        const alreadySelected = selectedNodeIds.length === 1 && selectedNodeIds[0] === selectId;
        if (alreadySelected) {
          setSelectedNodeIds([]);
          setInspecting(null);
          renderer?.setSelectedNode(null);
        } else {
          setSelectedNodeIds([selectId]);
          setInspecting({ nodeId: selectId, instanceId });
          // Widget tracks the clicked child mesh for positioning
          renderer?.setSelectedNode(nodeId);
        }
      }
      // Dragged position is captured by renderer's per-frame spatial store write-back

      pickedRef.current = null;
      groupOffsetsRef.current = null;
    }
  }, [instanceId, selectedNodeIds, setSelectedNodeIds, setInspecting]);

  // ─── Node drag / resize via pointer move ───
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!pickedRef.current) return;
    const renderer = getRenderer();
    const container = containerRef.current;
    if (!renderer || !container) return;

    didDragRef.current = true;

    if (pickedRef.current.resizing) {
      const dy = e.clientY - mousePosRef.current.y;
      mousePosRef.current = { x: e.clientX, y: e.clientY };

      const nodeId = pickedRef.current.nodeId;
      const node = definitionState?.nodes[nodeId];
      if (!node) return;
      const resolved = resolveVisualiser(node.type, stylesheet, node.endpoints);
      if (!resolved) return;
      const [, endpointId] = Object.entries(resolved.params)[0] ?? [];
      if (!endpointId) return;
      const ep = node.endpoints[endpointId];
      if (!ep) return;

      const nodeValues = instanceState[instanceId]?.values?.[node.id] ?? {};
      const payload = nodeValues[endpointId];
      const current = payload && typeof payload === 'object' && 'value' in payload
        ? payload.value
        : payload;
      const range = (ep.max ?? 1) - (ep.min ?? 0);
      const step = range * 0.002;
      const newVal = Math.max(ep.min ?? 0, Math.min(ep.max ?? 1, (current ?? ep.min ?? 0) - dy * step));
      tweakValue(instanceId, nodeId, endpointId, newVal);
    } else {
      // Normal drag: move on XZ plane
      const ndc = clientToNDC(e.clientX, e.clientY, container);
      const planeHit = renderer.raycastToPlane(ndc.x, ndc.y);
      if (planeHit) {
        if (groupOffsetsRef.current) {
          // Group drag: move all repeater siblings together
          for (const [id, offset] of groupOffsetsRef.current) {
            const m = renderer.getNodeMesh(id);
            if (m) {
              m.position.x = planeHit.x + offset.x;
              m.position.z = planeHit.z + offset.z;
            }
          }
        } else {
          // Individual drag
          const mesh = renderer.getNodeMesh(pickedRef.current.nodeId);
          if (mesh) {
            mesh.position.x = planeHit.x + dragOffsetRef.current.x;
            mesh.position.z = planeHit.z + dragOffsetRef.current.z;
          }
        }
      }
    }
  }, [definitionState, instanceId, instanceState, stylesheet, tweakValue]);

  // ─── Drop from NodeMenu ───
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('application/reactflow-type');
    if (!nodeType) return;

    const renderer = getRenderer();
    const container = containerRef.current;
    if (renderer && container) {
      const ndc = clientToNDC(e.clientX, e.clientY, container);
      const planeHit = renderer.raycastToPlane(ndc.x, ndc.y);
      if (planeHit) {
        addNode(instanceId, nodeType, planeHit.x / SCALE, planeHit.z / SCALE);
        return;
      }
    }
    // Fallback: place at origin
    addNode(instanceId, nodeType, 0, 0);
  }, [instanceId, addNode]);

  function handleToggleCamera() {
    const newCamera = activeCamera === 'plan' ? 'default' : 'plan';
    webRuntimeApi.setActivePerformanceCamera(instanceId, newCamera);
    setActiveCamera(newCamera);
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', height: '100%' }}
        />
      </div>
      <TweakablePanel overlay scopeDefinitionId={definitionId} />
      <button
        onClick={handleToggleCamera}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: activeCamera === 'plan' ? 'rgba(59, 130, 246, 0.8)' : 'rgba(15, 23, 42, 0.8)',
          color: '#e2e8f0',
          border: '1px solid #334155',
          borderRadius: 4,
          padding: '2px 8px',
          fontSize: 11,
          fontFamily: 'monospace',
          cursor: 'pointer',
          zIndex: 10,
        }}
      >
        {activeCamera === 'plan' ? 'orbit' : 'plan'}
      </button>
    </div>
  );
}
